import { useQuery, useMutation } from "@tanstack/react-query";
import { plainTextToHtml } from "@/lib/utils";
import { useParams, Link, useLocation } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Clock, Eye, Lock, MessageCircle, Send, CheckCircle2, BookOpen, FileText, Newspaper, Maximize2, CheckCheck } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { format } from "date-fns";
import { Helmet } from "react-helmet-async";
import { ArticleView } from "@/pages/article";
import { useState, type ReactNode } from "react";
import type { Article, Company, Person, ArticlePreviewToken, MediaAsset, MediaVariant, ImageSizePreset } from "@shared/schema";

interface ArticleWithRelations extends Article {
  companies?: Company[];
  people?: Person[];
}

interface GalleryImage {
  id: string;
  mediaAssetId: string;
  isHero: boolean;
  sortOrder: number;
  caption?: string | null;
  asset?: MediaAsset;
  variants?: Array<MediaVariant & { preset?: ImageSizePreset }>;
}

interface PreviewData {
  article: ArticleWithRelations;
  galleryImages: GalleryImage[];
  previewToken: ArticlePreviewToken;
}

function PreviewBanner({ token, mode, onModeChange }: { token: ArticlePreviewToken; mode: "article" | "dps"; onModeChange: (m: "article" | "dps") => void }) {
  const isExpiring = token.expiresAt && new Date(token.expiresAt).getTime() - Date.now() < 24 * 60 * 60 * 1000;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-4 py-3">
      <div className="max-w-screen-xl mx-auto flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-medium text-sm">
          <Eye className="h-4 w-4 flex-shrink-0" />
          Preview Mode
        </div>
        <Separator orientation="vertical" className="h-4 bg-amber-300 dark:bg-amber-700 hidden sm:block" />
        <p className="text-xs text-amber-700 dark:text-amber-400">
          This is a private preview link. The article is not yet publicly published.
        </p>
        {token.label && (
          <>
            <Separator orientation="vertical" className="h-4 bg-amber-300 dark:bg-amber-700 hidden sm:block" />
            <span className="text-xs text-amber-600 dark:text-amber-500">
              Link: <span className="font-medium">{token.label}</span>
            </span>
          </>
        )}
        {token.expiresAt && (
          <>
            <Separator orientation="vertical" className="h-4 bg-amber-300 dark:bg-amber-700 hidden sm:block" />
            <span className={`text-xs flex items-center gap-1 ${isExpiring ? "text-red-600 dark:text-red-400 font-medium" : "text-amber-600 dark:text-amber-500"}`}>
              <Clock className="h-3 w-3" />
              Expires {format(new Date(token.expiresAt), "PPP")}
            </span>
          </>
        )}
        <div className="ml-auto flex items-center gap-1 rounded-lg border border-amber-300 dark:border-amber-700 bg-white/60 dark:bg-black/20 p-0.5">
          <button
            onClick={() => onModeChange("article")}
            className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              mode === "article"
                ? "bg-amber-700 dark:bg-amber-600 text-white"
                : "text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            }`}
            data-testid="button-mode-article"
          >
            <FileText className="h-3 w-3" />
            Article
          </button>
          <button
            onClick={() => onModeChange("dps")}
            className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              mode === "dps"
                ? "bg-amber-700 dark:bg-amber-600 text-white"
                : "text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            }`}
            data-testid="button-mode-dps"
          >
            <Newspaper className="h-3 w-3" />
            Magazine DPS
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewError({ status, message }: { status: number; message: string }) {
  const isExpiredOrRevoked = status === 410;
  const isNotFound = status === 404;

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="rounded-full bg-muted p-6">
              {isExpiredOrRevoked ? (
                <Lock className="h-12 w-12 text-muted-foreground" />
              ) : (
                <AlertTriangle className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">
              {isNotFound ? "Preview Not Found" : isExpiredOrRevoked ? "Preview Unavailable" : "Something Went Wrong"}
            </h1>
            <p className="text-muted-foreground">{message}</p>
          </div>
          <Button asChild variant="outline">
            <Link href="/">Return to Home</Link>
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function PreviewCommentSection({ token }: { token: string }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localComments, setLocalComments] = useState<Array<{ id: string; authorName: string; authorEmail: string; body: string; createdAt: string }>>([]);

  const { data: comments = [], refetch } = useQuery<Array<{ id: string; authorName: string; authorEmail: string; body: string; createdAt: string }>>({
    queryKey: ["/api/preview", token, "comments"],
    queryFn: async () => {
      const res = await fetch(`/api/preview/${token}/comments`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 0,
  });

  const allComments = [...localComments, ...comments.filter(c => !localComments.find(l => l.id === c.id))];

  const mutation = useMutation({
    mutationFn: async () => {
      const optimisticId = `optimistic-${Date.now()}`;
      const optimisticComment = {
        id: optimisticId,
        authorName: name.trim(),
        authorEmail: email.trim(),
        body: body.trim(),
        createdAt: new Date().toISOString(),
      };
      setLocalComments(prev => [optimisticComment, ...prev]);
      try {
        const res = await fetch(`/api/preview/${token}/comments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ authorName: name.trim(), authorEmail: email.trim(), body: body.trim() }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({ message: "Failed to submit" }));
          setLocalComments(prev => prev.filter(c => c.id !== optimisticId));
          throw new Error(data.message);
        }
        const confirmed = await res.json();
        setLocalComments(prev => prev.map(c => c.id === optimisticId ? confirmed : c));
        return confirmed;
      } catch (err) {
        setLocalComments(prev => prev.filter(c => c.id !== optimisticId));
        throw err;
      }
    },
    onSuccess: () => {
      setSubmitted(true);
      setName("");
      setEmail("");
      setBody("");
      setError(null);
      refetch();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const canSubmit = name.trim() && email.trim() && body.trim();

  return (
    <div className="border-t bg-muted/30 py-10">
      <div className="max-w-2xl mx-auto px-4 space-y-6">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <MessageCircle className="h-4 w-4 text-muted-foreground" />
          Leave Feedback
        </div>
        {submitted ? (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Feedback sent!</p>
              <p className="text-xs text-green-700 dark:text-green-400">Thank you for reviewing this article. Your feedback has been submitted.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="preview-comment-name">
                  Your name
                </label>
                <Input
                  id="preview-comment-name"
                  placeholder="Jane Smith"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="input-preview-comment-name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="preview-comment-email">
                  Email address
                </label>
                <Input
                  id="preview-comment-email"
                  type="email"
                  placeholder="jane@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="input-preview-comment-email"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="preview-comment-body">
                Feedback
              </label>
              <Textarea
                id="preview-comment-body"
                placeholder="Share your thoughts, corrections, or suggestions..."
                rows={4}
                value={body}
                onChange={e => setBody(e.target.value)}
                data-testid="input-preview-comment-body"
              />
            </div>
            {error && (
              <p className="text-xs text-destructive">{error}</p>
            )}
            <Button
              size="sm"
              className="gap-2"
              disabled={!canSubmit || mutation.isPending}
              onClick={() => mutation.mutate()}
              data-testid="button-submit-preview-comment"
            >
              {mutation.isPending ? (
                <span className="animate-spin text-xs">⋯</span>
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Send Feedback
            </Button>
          </div>
        )}

        {allComments.length > 0 && (
          <div className="space-y-3 pt-4 border-t">
            <p className="text-sm font-medium text-muted-foreground">
              Feedback submitted ({allComments.length})
            </p>
            <div className="space-y-3" data-testid="preview-comments-list">
              {allComments.map(c => (
                <div key={c.id} className="rounded-lg border bg-background p-4 space-y-1" data-testid={`preview-comment-item-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{c.authorName}</p>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(c.createdAt), "PPp")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{c.authorEmail}</p>
                  <p className="text-sm whitespace-pre-wrap break-words pt-1">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface AdBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleId?: string;
}

function AdBookingModal({ open, onOpenChange, articleId }: AdBookingModalProps) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/public/ad-booking-enquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          contactName: name.trim(),
          contactEmail: email.trim(),
          contactCompany: company.trim() || undefined,
          message: message.trim() || undefined,
          articleId: articleId || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: "Failed to submit" }));
        throw new Error(data.message);
      }
      return res.json();
    },
    onSuccess: () => {
      setSubmitted(true);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const canSubmit = name.trim() && email.trim();

  const handleClose = () => {
    if (!mutation.isPending) {
      onOpenChange(false);
      setTimeout(() => {
        setSubmitted(false);
        setName("");
        setCompany("");
        setEmail("");
        setMessage("");
        setError(null);
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-ad-booking">
        <DialogHeader>
          <DialogTitle>Book This Advertising Space</DialogTitle>
          <DialogDescription>
            Submit your interest in the facing-page advertising slot on this article.
            Our team will be in touch with availability, formats, and pricing.
          </DialogDescription>
        </DialogHeader>
        {submitted ? (
          <div className="py-6 flex flex-col items-center gap-4 text-center">
            <div className="rounded-full bg-green-100 dark:bg-green-950/40 p-4">
              <CheckCheck className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="space-y-1">
              <p className="font-semibold">Enquiry Received!</p>
              <p className="text-sm text-muted-foreground">
                Thank you for your interest. A member of our team will be in touch shortly to discuss advertising options.
              </p>
            </div>
            <Button variant="outline" onClick={handleClose} data-testid="button-close-ad-booking">
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="ad-booking-name">
                  Your name <span className="text-destructive">*</span>
                </label>
                <Input
                  id="ad-booking-name"
                  placeholder="Jane Smith"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="input-ad-booking-name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="ad-booking-company">
                  Company
                </label>
                <Input
                  id="ad-booking-company"
                  placeholder="Acme Ltd"
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                  data-testid="input-ad-booking-company"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="ad-booking-email">
                Email address <span className="text-destructive">*</span>
              </label>
              <Input
                id="ad-booking-email"
                type="email"
                placeholder="jane@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="input-ad-booking-email"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="ad-booking-message">
                Message <span className="text-xs text-muted-foreground/60">(optional)</span>
              </label>
              <Textarea
                id="ad-booking-message"
                placeholder="Tell us about your campaign goals, target audience, or any questions..."
                rows={3}
                value={message}
                onChange={e => setMessage(e.target.value)}
                data-testid="input-ad-booking-message"
              />
            </div>
            {error && (
              <p className="text-xs text-destructive">{error}</p>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={handleClose} disabled={mutation.isPending} data-testid="button-cancel-ad-booking">
                Cancel
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={!canSubmit || mutation.isPending}
                className="gap-2"
                data-testid="button-submit-ad-booking"
              >
                {mutation.isPending ? (
                  <span className="animate-spin">⋯</span>
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send Enquiry
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DpsSpreadProps {
  article: ArticleWithRelations;
  galleryImages: GalleryImage[];
  commentSection: ReactNode;
}

function DpsSpread({ article, galleryImages, commentSection }: DpsSpreadProps) {
  const [bookingOpen, setBookingOpen] = useState(false);

  const heroImage = galleryImages.find(g => g.isHero) ?? galleryImages[0];
  const heroUrl = heroImage?.asset?.url ?? heroImage?.asset?.originalUrl;

  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
      <div className="px-4 py-6">
        <div className="text-center mb-4 space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Magazine Double-Page Spread Preview</p>
          <p className="text-xs text-muted-foreground">This layout approximates how the article would appear across two facing pages in print. Scroll horizontally on smaller screens.</p>
        </div>

        {/* DPS spread — always side-by-side; overflow-x scroll on small screens */}
        <div className="overflow-x-auto pb-2" data-testid="dps-spread-container">
          {/*
            Each "page" is A4-ish: width 420px, aspect ~1:1.41.
            Two pages = 840px minimum spread width.
          */}
          <div className="flex mx-auto shadow-2xl rounded overflow-hidden" style={{ minWidth: "840px", maxWidth: "1200px" }}>
            {/* LEFT PAGE — Article content */}
            <div
              className="flex-1 bg-white dark:bg-gray-950 border-r border-gray-300 dark:border-gray-700 flex flex-col"
              style={{ minWidth: "420px" }}
            >
              {/* Page header */}
              <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-3 flex items-center justify-between flex-shrink-0">
                <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium">Feature</span>
                <span className="text-[10px] text-muted-foreground">FM World Magazine</span>
              </div>

              {/* Article content — scrollable within the page */}
              <div className="flex-1 px-6 py-6 space-y-4 overflow-y-auto" style={{ maxHeight: "75vh" }}>
                {heroUrl && (
                  <img
                    src={heroUrl}
                    alt={article.title}
                    className="w-full object-cover rounded"
                    style={{ maxHeight: "240px" }}
                    data-testid="dps-article-hero"
                  />
                )}
                <div className="space-y-2">
                  <h1 className="text-xl font-bold leading-tight" data-testid="dps-article-title">
                    {article.title}
                  </h1>
                  {article.excerpt && (
                    <p className="text-sm font-medium text-muted-foreground leading-relaxed" data-testid="dps-article-excerpt">
                      {article.excerpt}
                    </p>
                  )}
                </div>
                {article.content && (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: plainTextToHtml(article.content) }}
                    data-testid="dps-article-body"
                  />
                )}
                {article.companies && article.companies.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2 border-t">
                    {article.companies.map(c => (
                      <Badge key={c.id} variant="secondary" className="text-xs">{c.name}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Page footer / page number */}
              <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-2 flex-shrink-0">
                <span className="text-[10px] text-muted-foreground">24</span>
              </div>
            </div>

            {/* RIGHT PAGE — Facing page ad placeholder */}
            <div
              className="flex-1 bg-white dark:bg-gray-950 flex flex-col"
              style={{ minWidth: "420px" }}
              data-testid="dps-facing-page"
            >
              {/* Page header */}
              <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-3 flex items-center justify-between flex-shrink-0">
                <span className="text-[10px] text-muted-foreground">FM World Magazine</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium">Advertisement</span>
              </div>

              {/* Ad slot — centred in remaining space */}
              <div className="flex-1 flex flex-col items-center justify-center p-6 gap-5">
                {/* Placeholder image frame */}
                <div
                  className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center gap-5 bg-gray-50 dark:bg-gray-900/50 py-8"
                  data-testid="dps-ad-placeholder"
                  role="img"
                  aria-label="Full-page advertising slot placeholder"
                >
                  {/* Placeholder image representation */}
                  <div
                    className="w-full mx-6 rounded bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center"
                    style={{ maxWidth: "320px", height: "180px" }}
                  >
                    <Maximize2 className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                  </div>

                  <div className="text-center space-y-1 px-6">
                    <p className="text-base font-semibold text-foreground/80">Full-Page Advertisement</p>
                    <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
                      This page is reserved for an advertising placement alongside this feature article.
                    </p>
                  </div>

                  {/* Ad specs */}
                  <div className="grid grid-cols-2 gap-2 px-6 w-full max-w-xs">
                    {[
                      { label: "Format", value: "Full Page" },
                      { label: "Bleed", value: "3mm" },
                      { label: "Trim Size", value: "210 × 297mm" },
                      { label: "Position", value: "Right-Hand" },
                    ].map(spec => (
                      <div key={spec.label} className="rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2 text-center">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{spec.label}</p>
                        <p className="text-xs font-semibold">{spec.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="text-center space-y-2 px-6">
                    <p className="text-xs text-muted-foreground">
                      Advertise here to reach readers engaged with this content.
                    </p>
                    <Button
                      onClick={() => setBookingOpen(true)}
                      className="gap-2"
                      data-testid="button-book-ad-space"
                    >
                      <BookOpen className="h-4 w-4" />
                      Book This Space
                    </Button>
                  </div>
                </div>
              </div>

              {/* Page footer / page number */}
              <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-2 text-right flex-shrink-0">
                <span className="text-[10px] text-muted-foreground">25</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback section below DPS */}
        <div className="mt-6">
          {commentSection}
        </div>
      </div>

      <AdBookingModal open={bookingOpen} onOpenChange={setBookingOpen} articleId={article.id} />
    </div>
  );
}

export default function PreviewPage() {
  const { token } = useParams<{ token: string }>();
  const [location, setLocation] = useLocation();

  const searchParams = new URLSearchParams(location.includes("?") ? location.split("?")[1] : "");
  const urlMode = searchParams.get("mode");
  const mode: "article" | "dps" = urlMode === "dps" ? "dps" : "article";

  const handleModeChange = (newMode: "article" | "dps") => {
    const base = `/preview/${token}`;
    if (newMode === "dps") {
      setLocation(`${base}?mode=dps`);
    } else {
      setLocation(base);
    }
  };

  const { data, isLoading, error } = useQuery<PreviewData>({
    queryKey: ["/api/preview", token],
    queryFn: async () => {
      const res = await fetch(`/api/preview/${token}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: "Unknown error" }));
        const err = new Error(body.message || "Failed to load preview") as Error & { status: number };
        err.status = res.status;
        throw err;
      }
      return res.json();
    },
    staleTime: 0,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <main className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full space-y-8">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-64 w-full" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error) {
    const err = error as Error & { status?: number };
    return <PreviewError status={err.status ?? 500} message={err.message} />;
  }

  if (!data) {
    return <PreviewError status={404} message="Preview not found." />;
  }

  const { article, galleryImages, previewToken } = data;

  const commentSection = <PreviewCommentSection token={previewToken.token} />;

  return (
    <>
      <Helmet>
        <title>{article.title} — Preview | FM World</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {mode === "dps" ? (
        <div className="min-h-screen flex flex-col">
          <SiteHeader />
          <PreviewBanner token={previewToken} mode={mode} onModeChange={handleModeChange} />
          <DpsSpread
            article={article}
            galleryImages={galleryImages}
            commentSection={commentSection}
          />
        </div>
      ) : (
        <ArticleView
          article={article}
          galleryImages={galleryImages}
          otherArticles={[]}
          banner={<PreviewBanner token={previewToken} mode={mode} onModeChange={handleModeChange} />}
          footerSlot={commentSection}
        />
      )}
    </>
  );
}
